/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { ChartSeriesData, DataAxisProps, Group } from '../UNSAFE_visTypes/chart';
import { getTime } from '#PRIVATE_Axis/timeAxis/timeAxisInfo';
import { BarSeriesData } from '#UNSAFE_BarChart/barChart.types';

/**
 * Returns the min and max value among the items rendered in the viewport.
 * @param series The array of chart series.
 * @param viewportMin The current viewport min.
 * @param viewportMax The current viewport max.
 * @returns
 */
export function getDataMinMax<
  K extends string | number,
  D extends { x?: number | string; y?: number; widthWeight?: number; value?: number; id: K }
>(
  series: ChartSeriesData<K, D>[],
  groups: Group[],
  xStartIndex: number,
  xEndIndex: number,
  getDataItem: (seriesIndex: number, groupIndex: number) => D | undefined,
  isStacked: boolean,
  isLog: boolean,
  hiddenSet: Set<K>,
  hideAndShowBehavior: 'none' | 'withRescale' | 'withoutRescale',
  getStackGroup?: (s: ChartSeriesData<K, D>) => string | undefined
) {
  let yDataMin = Number.MAX_VALUE;
  let yDataMax = -Number.MAX_VALUE;
  let xMin = Number.MAX_VALUE;
  let xMax = -Number.MAX_VALUE;
  let yMin = Number.MAX_VALUE;
  let yMax = -Number.MAX_VALUE;
  let y2DataMin = Number.MAX_VALUE;
  let y2DataMax = -Number.MAX_VALUE;
  let y2Min = Number.MAX_VALUE;
  let y2Max = -Number.MAX_VALUE;
  let isY2AxisRendered = false;
  let isYAxisRendered = false;
  let hasWidthWeightValue = false;
  const barWidths: number[] = [];
  const idToDataMap = new Map<K, D>();
  const stackGroupMinMap = new Map<string, number>();
  const stackGroupMaxMap = new Map<string, number>();
  for (let groupIndex = 0; groupIndex < groups.length; groupIndex++) {
    const stackCumuls = new Map<string, { pos: number; neg: number }>();
    series.forEach((chartSeries: BarSeriesData<K, D>, seriesIndex: number) => {
      const isAssociatedToY2 = chartSeries?.associatedYAxis === 'y2';
      isY2AxisRendered = isY2AxisRendered || !!isAssociatedToY2;
      isYAxisRendered = isYAxisRendered || !isAssociatedToY2;

      const item = getDataItem(seriesIndex, groupIndex);
      const itemHasWidthWeightValue = item && 'widthWeight' in item;
      hasWidthWeightValue = hasWidthWeightValue || itemHasWidthWeightValue!;
      if (itemHasWidthWeightValue && item?.widthWeight != null) {
        barWidths.push(Math.max(0, item.widthWeight));
      }
      const itemHasValue = item && 'value' in item;
      if (
        !item ||
        (itemHasValue && item.value == null && item.x == null && 'y' in item && item.y == null)
      ) {
        return;
      }
      idToDataMap.set(item.id, item);
      if (groupIndex >= xStartIndex && groupIndex <= xEndIndex) {
        const skipData =
          item?.id != null && hiddenSet.has(item.id) && hideAndShowBehavior === 'withRescale';
        if (skipData || (itemHasValue && item.value !== undefined && item.value <= 0 && isLog)) {
          return;
        }
        if (isStacked) {
          const stackGroup = getStackGroup?.(chartSeries);
          const axis = isAssociatedToY2 ? 'y2' : 'y';
          const key = stackGroup + '_' + axis;
          if (!stackCumuls.has(key)) {
            stackCumuls.set(key, { pos: 0, neg: 0 });
          }
          const cumul = stackCumuls.get(key)!;
          if (itemHasValue && item.value !== undefined) {
            if (item.value > 0) {
              cumul.pos += item.value;
            } else {
              cumul.neg += item.value;
            }
          }
        } else {
          if (itemHasValue && item.value !== undefined) {
            if (isAssociatedToY2) {
              y2DataMin = Math.min(y2DataMin, item.value);
              y2DataMax = Math.max(y2DataMax, item.value);
            } else {
              yDataMin = Math.min(yDataMin, item.value);
              yDataMax = Math.max(yDataMax, item.value);
            }
          }
          if ('x' in item && item.x !== undefined && 'y' in item && item.y !== undefined) {
            xMin = Math.min(xMin, item.x as number);
            xMax = Math.max(xMax, item.x as number);

            if (isAssociatedToY2) {
              y2Min = Math.min(y2Min, item.y);
              y2Max = Math.max(y2Max, item.y);
            } else {
              yMin = Math.min(yMin, item.y);
              yMax = Math.max(yMax, item.y);
            }
          }
        }
      }
    });

    if (isStacked && groupIndex >= xStartIndex && groupIndex <= xEndIndex) {
      for (const [key, cumul] of stackCumuls) {
        const groupMin = isLog ? cumul.pos : cumul.neg;
        const groupMax = cumul.pos;
        if (!stackGroupMinMap.has(key)) {
          stackGroupMinMap.set(key, Number.MAX_VALUE);
          stackGroupMaxMap.set(key, -Number.MAX_VALUE);
        }
        stackGroupMinMap.set(key, Math.min(stackGroupMinMap.get(key)!, groupMin));
        stackGroupMaxMap.set(key, Math.max(stackGroupMaxMap.get(key)!, groupMax));
      }
    }
  }

  // Combine stack group min/max
  for (const [key, minVal] of stackGroupMinMap) {
    const maxVal = stackGroupMaxMap.get(key)!;
    const axis = key.split('_')[1];
    if (axis === 'y2') {
      if (minVal !== Number.MAX_VALUE) {
        y2DataMin = Math.min(y2DataMin, minVal);
      }
      if (maxVal !== -Number.MAX_VALUE) {
        y2DataMax = Math.max(y2DataMax, maxVal);
      }
    } else {
      if (minVal !== Number.MAX_VALUE) {
        yDataMin = Math.min(yDataMin, minVal);
      }
      if (maxVal !== -Number.MAX_VALUE) {
        yDataMax = Math.max(yDataMax, maxVal);
      }
    }
  }

  return {
    yDataMin,
    y2DataMin,
    yDataMax,
    y2DataMax,
    xMin,
    xMax,
    yMin,
    y2Min,
    yMax,
    y2Max,
    idToDataMap,
    isY2AxisRendered,
    isYAxisRendered,
    hasWidthWeightValue,
    barWidths
  };
}

/**
 * Returns the dataMin and dataMax, used for axis extend calculations, from the chart data.
 * @param series
 * @param hiddenIds
 * @param xStartIndex
 * @param xEndIndex
 * @param yAxis
 * @returns
 */
export function getDataExtent<
  K extends string | number,
  D extends { x?: number | string; y?: number; widthWeight?: number; value?: number; id: K }
>(
  series: BarSeriesData<K, D>[],
  groups: Group[],
  getDataItem: (seriesIndex: number, groupIndex: number) => D | undefined,
  xStartIndex: number,
  xEndIndex: number,
  isStacked: boolean,
  hiddenSet: Set<K>,
  hideAndShowBehavior: 'none' | 'withRescale' | 'withoutRescale',
  yAxis?: DataAxisProps,
  y2Axis?: DataAxisProps,
  getStackGroup?: (s: ChartSeriesData<K, D>) => string | undefined
) {
  let yDataMin = yAxis?.dataMin;
  let yDataMax = yAxis?.dataMax;
  let y2DataMin = y2Axis?.dataMin;
  let y2DataMax = y2Axis?.dataMax;
  //TODO: For performance optimization we can return early if we do not need idToDataMap i.e when selectionMode is off.
  // if (dataMin != null && dataMax != null) {
  //   return { dataMin, dataMax };
  // }
  const data = getDataMinMax<K, D>(
    series,
    groups,
    xStartIndex,
    xEndIndex,
    getDataItem,
    isStacked,
    yAxis?.scale === 'log',
    hiddenSet,
    hideAndShowBehavior,
    getStackGroup
  );

  if (yDataMin == null) {
    yDataMin = data.yDataMin;
  }
  if (yDataMax == null) {
    yDataMax = data.yDataMax;
  }

  if (y2DataMin == null) {
    y2DataMin = data.y2DataMin;
  }

  if (y2DataMax == null) {
    y2DataMax = data.y2DataMax;
  }
  return {
    yDataMax,
    yDataMin,
    y2DataMax,
    y2DataMin,
    xMin: data.xMin,
    xMax: data.xMax,
    yMin: data.yMin,
    yMax: data.yMax,
    y2Min: data.y2Min,
    y2Max: data.y2Max,
    idToDataMap: data.idToDataMap,
    isY2AxisRendered: data.isY2AxisRendered,
    isYAxisRendered: data.isYAxisRendered,
    hasWidthWeightValue: data.hasWidthWeightValue,
    barWidths: data.barWidths
  };
}

/**
 * For a given groupIndex gIndex, and seriesIndex sIndex, returns the next groupIndex that has not null data.
 * @param gIndex The groupIndex.
 * @param sIndex The seriesIndex.
 * @param groups The groups arrray.
 * @param getDataItem The function that returns data item for given seriesIndex and groupIndex.
 * @returns
 */
function findNextGroupWithData<D>(
  gIndex: number,
  sIndex: number,
  groups: Group[],
  getDataItem: (sIndex: number, gIndex: number) => D | undefined
) {
  let right = gIndex;
  while (right < groups.length) {
    const hasData = !!getDataItem(sIndex, right);
    if (hasData) {
      return right;
    }
    right += 1;
  }
  return -1;
}

/**
 * For a given groupIndex gIndex, and seriesIndex sIndex, returns the prev groupIndex that has not null data.
 * @param gIndex The groupIndex.
 * @param sIndex The seriesIndex.
 * @param groups The groups arrray.
 * @param getDataItem The function that returns data item for given seriesIndex and groupIndex.
 * @returns
 */
function findPreviousGroupWithData<D>(
  gIndex: number,
  sIndex: number,
  getDataItem: (sIndex: number, gIndex: number) => D | undefined
) {
  let left = gIndex;
  while (left >= 0) {
    const hasData = !!getDataItem(sIndex, left);
    if (hasData) {
      return left;
    }
    left -= 1;
  }
  return -1;
}

/**
 * Finds the nearest data group/item for the given x axis coordinate.
 * @param x The x coordinate.
 * @param groups The groups array.
 * @param series The series array.
 * @param getDataItem The function that returns data item for given seriesIndex and groupIndex.
 * @returns
 */
export function findNearestRegularAxisIndex<K, D>(
  x: number,
  groups: Group[],
  series: ChartSeriesData<K, D>[],
  getDataItem: (sIndex: number, gIndex: number) => D | undefined
) {
  const keyFunc = (index: number) => getTime(groups[index].id);

  if (groups.length < 2) {
    return 0;
  }

  if (keyFunc(0) > x) {
    return Math.min(
      ...series.map((_, sIndex) => findNextGroupWithData<D>(0, sIndex, groups, getDataItem))
    );
  }

  if (keyFunc(groups.length - 1) < x) {
    return Math.max(
      ...series.map((_, sIndex) =>
        findPreviousGroupWithData(groups.length - 1, sIndex, getDataItem)
      )
    );
  }

  const groupIndex = findGroup(0, groups.length - 1, x, keyFunc);

  const prevIndex = Math.max(
    ...series.map((_, sIndex) => findPreviousGroupWithData(groupIndex, sIndex, getDataItem))
  );

  if (prevIndex === groupIndex) return prevIndex;

  const nextIndex = Math.min(
    ...series.map((_, sIndex) => findNextGroupWithData<D>(groupIndex, sIndex, groups, getDataItem))
  );

  if (keyFunc(nextIndex) - x < x - keyFunc(prevIndex)) {
    return nextIndex;
  }
  return prevIndex;
}

/**
 * Finds the nearest data group/item for the given x axis coordinate in mixed frequency time axis chart.
 * @param x The x axis coordinate
 * @param groups The chart groups.
 * @param series The chart series.
 * @param getDataItem The function that returns data item for each series and groups.
 */
export function getNearestMixedFrequencyDataItem<K, D extends { x?: number | string }>(
  target: number,
  series: ChartSeriesData<K, D>[],
  groups: Group[],
  getDataItem: (seriesIndex: number, groupIndex: number) => D | undefined
) {
  /**
   * first find the data items in each series closest to the given value and
   * then find the closest among them.
   * TODO: make this O(log(n))
   */
  return series.map((_, seriesIndex) => {
    let min = Number.MAX_VALUE;
    let minIndex = 0;
    for (let groupIndex = 0; groupIndex < groups.length; groupIndex++) {
      const x = getDataItem(seriesIndex, groupIndex)?.x;
      if (!x) {
        continue;
      }
      const time = getTime(x);
      const dist = Math.abs(target - time);
      if (dist < min) {
        min = dist;
        minIndex = groupIndex;
      }
    }
    return { seriesIndex, groupIndex: minIndex };
  });
}

/**
 * For a given target value, binary searches the index between min and max. The keyFunc returns the
 * value for each given index.
 * @param min The minimum index to search.
 * @param max The maximum index to search.
 * @param target The target value to find the neareast index to.
 * @param keyFunc The function that returns the target value for each given index.
 * @returns
 */
function findGroup(min: number, max: number, target: number, keyFunc: (index: number) => number) {
  let left = min;
  let right = max;

  const foundIndex = (ll: number, rr: number) => {
    return rr - ll === 1 && keyFunc(ll) <= target && target <= keyFunc(rr);
  };

  let moveRight = 0;
  let step = Math.floor((right - left) / 2);
  while (!foundIndex(left, right)) {
    const newIndex = moveRight ? Math.floor(left + step) : Math.ceil(right - step);
    const diff = target - keyFunc(newIndex);
    if (0 <= diff) {
      left = newIndex;
      moveRight = moveRight ? 0 : 1;
    } else {
      right = newIndex;
    }
    step = Math.floor((right - left) / 2);
  }
  return target - keyFunc(left) < keyFunc(right) - target ? left : right;
}

/**
 * Returns true if data exists.
 * @param series The series array
 * @param groups The groups array
 */
export function hasData<K, D>(series: ChartSeriesData<K, D>[], groups: Group[]) {
  return series.length > 0 && groups.length > 0;
}

/**
 * Returns true if the data is invalid.
 * @param timeAxisType The time axis type
 * @param series The series array
 * @param groups The groups array
 * @param getDataItem The function that returns data item
 */
export function hasInvalidData<K, D extends { x?: string | number }>(
  timeAxisType: 'mixedFrequency' | 'skipGaps' | 'enabled' | undefined,
  series: ChartSeriesData<K, D>[],
  groups: Group[],
  getDataItem: (seriesIndex: number, groupIndex: number, groupIndexPath: number[]) => D | undefined
) {
  return hasInvalidTimeData(timeAxisType, series, groups, getDataItem);
}

/**
 * Returns true if there is a time axis and data doesn't have valid numerical data for it.
 * @param timeAxisType The time axis type
 * @param series The series array
 * @param groups The groups array
 * @param getDataItem The function that returns data item
 */
function hasInvalidTimeData<K, D extends { x?: string | number }>(
  timeAxisType: 'mixedFrequency' | 'skipGaps' | 'enabled' | undefined,
  series: ChartSeriesData<K, D>[],
  groups: Group[],
  getDataItem: (seriesIndex: number, groupIndex: number, groupIndexPath: number[]) => D | undefined
) {
  if (timeAxisType === undefined) {
    return false;
  }

  if (series.length === 0 || groups.length === 0) {
    return true;
  }

  if (timeAxisType === 'mixedFrequency') {
    // Check that all the x values are valid date strings
    for (let s = 0; s < series.length; s++) {
      for (let g = 0; g < groups.length; g++) {
        const item = getDataItem(s, g, [g]);
        if (item && (typeof item.x !== 'string' || isNaN(Date.parse(item.x)))) {
          return true;
        }
      }
    }
  } else {
    // Check that all group ids are valid date strings
    for (let i = 0; i < groups.length; i++) {
      const groupId = groups[i].id;
      if (isNaN(Date.parse(groupId))) {
        return true;
      }
    }
  }

  return false;
}
